// Drawer «Acceso de {nombre}» de Administración · Equipo y accesos
// (handoff §3 «Editar acceso»): Rol y Correo en kv-grid, la marca de
// afiliación del supervisor cuando la org tiene más de una (la misma
// mutación que antes vivía inline en la tabla vieja, ADR-0165), y el
// selector de tiendas con el diff `--kept/--added/--removed` sobre las
// tiendas que la persona tiene hoy. Pie Cancelar · «Guardar acceso»,
// deshabilitado hasta que hay algo que cambiar.
//
// Guardar manda el diff a PUT /organizations/me/members/:userId/scope.
// Si una tienda ya tiene a otro titular (409 ASSET_HOLDER_OCCUPIED) el
// error va inline con «Reemplazar», que reintenta con `replace: true`:
// es el único camino de error con botón propio que pide el encargo.
//
// Las zonas (asset_groups.supervisor_user_id) no se editan acá: su
// responsable se cambia en {Tiendas y zonas}; el selector sólo lista las
// tiendas directas.

import { useMemo, useState } from "react";
import type { UseQueryResult } from "@tanstack/react-query";
import type { AssignableAssetsResponse, OrgMember, RecipeMeResponse } from "@bitacorapro/api-types";
import { brandIdentityOf } from "@bitacorapro/core-engine";
import { Drawer } from "../../../components/Drawer";
import { QueryBoundary } from "../../../components/QueryBoundary";
import { StorePicker } from "../../../components/StorePicker";
import { roleLabelFor } from "../../../lib/admin/words";
import { useSetBrandAffiliation } from "../../../lib/api/queries";
import { StorePickerSkeleton } from "../../../routes/EquipoSkeleton";
import {
  hasScopeChanges,
  INVITE_ERROR_MESSAGES,
  occupiedDetails,
  occupiedMessage,
  SCOPE_HINT,
  SCOPE_SAVED_TOAST,
  scopeDiff,
  scopeErrorMessage,
  type EquipoWords,
  type OccupiedDetails,
} from "./model";
import { useUpdateMemberScope } from "./queries";
import { ApiError } from "../../../lib/api/error";

export type AccesoDrawerProps = {
  member: OrgMember;
  recipe: RecipeMeResponse;
  words: EquipoWords;
  /** Las tiendas directas de hoy (member-scopes ∪ scopeAssetId). */
  currentAssetIds: readonly string[];
  /** id → nombre, para la tienda ocupada del 409 cuando no trae unit. */
  names: ReadonlyMap<string, string>;
  /** GET /assets/assignable: la fuente del selector. */
  storesQuery: UseQueryResult<AssignableAssetsResponse>;
  /** Las marcas de la org (GET /organizations/me.brands); ausentes para
   *  un caller sin alcance de organización. */
  orgBrands: { id: string; name: string }[] | undefined;
  /** La marca de afiliación actual del integrante, si se conoce. */
  affiliationBrandId: string | undefined;
  /** Quien puede cambiar la afiliación (rol de alcance organización). */
  canEditAffiliation: boolean;
  mode: "single" | "multi";
  onClose: () => void;
  /** Mensaje del toast que muestra la pantalla. */
  onSaved: (message: string) => void;
};

export function AccesoDrawer({
  member,
  recipe,
  words: w,
  currentAssetIds,
  names,
  storesQuery,
  orgBrands,
  affiliationBrandId,
  canEditAffiliation,
  mode,
  onClose,
  onSaved,
}: AccesoDrawerProps) {
  const originalIds = useMemo(() => [...currentAssetIds], [currentAssetIds]);
  const [selectedIds, setSelectedIds] = useState<string[]>(originalIds);
  const [error, setError] = useState<string | null>(null);
  const [occupied, setOccupied] = useState<OccupiedDetails | null>(null);
  const [affiliationError, setAffiliationError] = useState<string | null>(null);
  const update = useUpdateMemberScope();
  const setAffiliation = useSetBrandAffiliation();
  const pending = update.isPending;

  const roleLabel = roleLabelFor(member.role, recipe);
  const diff = scopeDiff(originalIds, selectedIds);
  const canSave = !pending && hasScopeChanges(diff);

  // La marca de afiliación sólo se muestra donde existe de verdad: el rol
  // la declara (brandIdentity "affiliation") y la org tiene más de una
  // marca. Con una sola, el servidor la rellena.
  const multiBrand = (orgBrands?.length ?? 0) > 1;
  const showAffiliation =
    canEditAffiliation &&
    multiBrand &&
    orgBrands !== undefined &&
    brandIdentityOf(recipe, member.role) === "affiliation";

  // Un supervisor sólo recibe tiendas de su marca (ADR-0165): el
  // selector ofrece esas, más las que ya tiene aunque no cuadren, para
  // poder quitárselas.
  function pickerStores(all: AssignableAssetsResponse["assets"]) {
    if (!showAffiliation || !affiliationBrandId) return all;
    const original = new Set(originalIds);
    return all.filter((s) => s.brandId === affiliationBrandId || original.has(s.id));
  }

  async function save(replace: boolean) {
    setError(null);
    setOccupied(null);
    if (!hasScopeChanges(diff)) return;
    try {
      await update.mutateAsync({
        userId: member.userId,
        body: replace ? { ...diff, replace: true } : diff,
      });
      onSaved(SCOPE_SAVED_TOAST);
      onClose();
    } catch (err) {
      const details = occupiedDetails(err);
      if (details) {
        setOccupied(details);
        return;
      }
      setError(scopeErrorMessage(err, w));
    }
  }

  function changeAffiliation(brandId: string) {
    if (brandId === "") return;
    setAffiliationError(null);
    setAffiliation.mutate(
      { userId: member.userId, brandId },
      {
        onSuccess: () => onSaved(SCOPE_SAVED_TOAST),
        onError: (err) =>
          setAffiliationError(
            (err instanceof ApiError ? INVITE_ERROR_MESSAGES[err.code] : undefined) ??
              `No pudimos guardar la ${w.brandLower}. Inténtalo de nuevo.`
          ),
      }
    );
  }

  const affiliationSelectId = `acceso-${member.userId}-marca`;
  const affiliationErrorId = `${affiliationSelectId}-error`;

  return (
    <Drawer
      title={`Acceso de ${member.displayName}`}
      onClose={onClose}
      footer={
        <>
          <button
            type="button"
            className="btn btn--ghost"
            title="Cerrar sin guardar cambios"
            disabled={pending}
            onClick={onClose}
          >
            Cancelar
          </button>
          <button
            type="button"
            className="btn btn--primary"
            title={`Guardar las ${w.assetManyLower} a las que accede ${member.displayName}`}
            disabled={!canSave}
            onClick={() => void save(false)}
          >
            {pending ? "Guardando…" : "Guardar acceso"}
          </button>
        </>
      }
    >
      <dl className="kv-grid ag-mb16">
        <dt>Rol</dt>
        <dd>{roleLabel}</dd>
        <dt>Correo</dt>
        <dd className="mono">{member.email}</dd>
      </dl>

      {showAffiliation && (
        <div className="field ag-field">
          <label className="field__label" htmlFor={affiliationSelectId}>
            {w.brand} del {roleLabel.toLocaleLowerCase("es-CL")}
          </label>
          <select
            id={affiliationSelectId}
            className="input"
            value={affiliationBrandId ?? ""}
            disabled={setAffiliation.isPending || pending}
            aria-invalid={affiliationError ? true : undefined}
            aria-describedby={affiliationError ? affiliationErrorId : undefined}
            onChange={(e) => changeAffiliation(e.target.value)}
          >
            <option value="" disabled>
              Sin {w.brandLower}
            </option>
            {orgBrands.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
          {affiliationError && (
            <span id={affiliationErrorId} className="auth-error" role="alert">
              {affiliationError}
            </span>
          )}
        </div>
      )}

      <QueryBoundary
        query={storesQuery}
        label={`las ${w.assetManyLower}`}
        kind="section"
        skeleton={<StorePickerSkeleton />}
        isEmpty={(data) => data.assets.length === 0}
        empty={
          <div className="store-picker store-picker--empty">
            <div className="store-picker__legend">
              No hay {w.assetManyLower} asignables todavía.
            </div>
          </div>
        }
      >
        {(data) => (
          <StorePicker
            stores={pickerStores(data.assets)}
            selectedIds={selectedIds}
            onChange={(next) => {
              setSelectedIds(next);
              setOccupied(null);
              setError(null);
            }}
            disabled={pending}
            originalIds={originalIds}
            addedLabel="Se agrega"
            removedLabel="Se quita"
            mode={mode}
            legend={`${w.assetMany} con acceso`}
            className="store-picker__list--tall"
          />
        )}
      </QueryBoundary>

      <p className="field__hint ag-mt8">
        {SCOPE_HINT} {w.assetManyLower}.
      </p>

      {occupied && (
        <div className="auth-error ag-mt8" role="alert">
          <span>{occupiedMessage(occupied, roleLabel, names, w)}</span>{" "}
          <button
            type="button"
            className="btn btn--secondary btn--sm"
            title={`Quitar esa ${w.assetOneLower} a su titular actual y dársela a ${member.displayName}`}
            disabled={pending}
            onClick={() => void save(true)}
          >
            Reemplazar
          </button>
        </div>
      )}
      {error && (
        <p className="auth-error ag-mt8" role="alert">
          {error}
        </p>
      )}
    </Drawer>
  );
}
